import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Gauge, Printer, Check, GripVertical, FileBarChart, Download } from "lucide-react";
import Modal from "@/components/Modal";

const money = (n) => Number(n || 0).toLocaleString();
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function DayPlanner() {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [dragI, setDragI] = useState(null);

  const [reportOpen, setReportOpen] = useState(false);
  const [rStart, setRStart] = useState(firstOfMonth());
  const [rEnd, setREnd] = useState(today);
  const [report, setReport] = useState(null);

  const load = (d) => api.get("/planner", { params: { on_date: d } }).then((r) => {
    setRows(r.data.rows.map((x) => ({ ...x, start_mile: x.start_mile ?? x.carry_start ?? "", end_mile: x.end_mile ?? "" })));
    setTotal(r.data.total_miles);
  }).catch(() => {});
  useEffect(() => { load(date); }, [date]);

  const setField = (i, k, v) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const commit = async (r) => {
    await api.put("/planner/entry", {
      on_date: date, item_id: r.item_id, note: r.note || "", done: !!r.done,
      start_mile: r.start_mile === "" || r.start_mile == null ? null : Number(r.start_mile),
      end_mile: r.end_mile === "" || r.end_mile == null ? null : Number(r.end_mile),
    });
    load(date);
  };
  const toggleDone = (i, r) => { const nr = { ...r, done: !r.done }; setField(i, "done", nr.done); commit(nr); };
  const rowMiles = (r) => {
    const s = Number(r.start_mile), e = Number(r.end_mile);
    return r.start_mile !== "" && r.end_mile !== "" && r.start_mile != null && r.end_mile != null && e >= s ? (e - s) : null;
  };

  const onDrop = (i) => {
    if (dragI == null || dragI === i) { setDragI(null); return; }
    setRows((rs) => {
      const next = [...rs];
      const [moved] = next.splice(dragI, 1);
      next.splice(i, 0, moved);
      api.put("/planner/order", { on_date: date, item_ids: next.map((r) => r.item_id) }).catch(() => {});
      return next;
    });
    setDragI(null);
  };

  const runReport = async () => {
    try {
      const { data } = await api.get("/planner/report", { params: { start: rStart, end: rEnd } });
      setReport(data);
    } catch (e) {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (reportOpen) runReport(); }, [reportOpen]);

  const reportCsv = () => {
    if (!report) return;
    const lines = [["Date", "Miles", "Stops"], ...report.rows.map((r) => [r.date, r.miles, r.items]), ["TOTAL", report.total_miles, ""]];
    const csv = lines.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `mileage-${report.start}_to_${report.end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const printReport = () => {
    if (!report) return;
    const body = report.rows.map((r) => `<tr><td>${r.date}</td><td style="text-align:right">${r.miles}</td><td style="text-align:right">${r.items}</td></tr>`).join("");
    const html = `<html><head><title>Mileage Report</title><style>body{font-family:system-ui,sans-serif;padding:40px;color:#1A1A18}h1{font-size:22px;margin:0}p{color:#666}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{border-bottom:1px solid #e2e2e2;padding:8px;font-size:13px}th{text-align:left;text-transform:uppercase;font-size:11px;letter-spacing:.1em;color:#777}tfoot td{font-weight:bold;border-top:2px solid #d36b46}</style></head><body><h1>Mileage Report</h1><p>${report.start} → ${report.end} · ${report.days} active day(s)</p><table><thead><tr><th>Date</th><th style="text-align:right">Miles</th><th style="text-align:right">Stops</th></tr></thead><tbody>${body}</tbody><tfoot><tr><td>Total miles</td><td style="text-align:right">${report.total_miles}</td><td></td></tr></tfoot></table></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 300);
  };

  const d = new Date(date + "T00:00:00");
  const weekday = d.toLocaleDateString(undefined, { weekday: "long" });
  const nice = d.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });

  return (
    <div data-testid="day-planner" className="planner-print">
      <div className="border border-[var(--line)] shadow-sm" style={{ backgroundColor: "#fdfbf0" }}>
        <div className="flex items-end justify-between px-6 sm:px-10 pt-6 pb-3 border-b-2 border-[#d36b46]/40">
          <div>
            <p className="font-serif text-3xl sm:text-4xl tracking-tight text-[#2A4B46]">{weekday}</p>
            <p className="label-mono text-[var(--text-2)] mt-1">{nice}</p>
          </div>
          <div className="text-right">
            <input
              data-testid="planner-date" type="date" value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-transparent border border-[var(--line)] h-9 px-2 text-sm font-mono"
            />
            <div className="flex gap-2 justify-end mt-2 print-hide">
              <button data-testid="planner-print" onClick={() => window.print()} className="flex items-center gap-1 h-8 px-2 text-xs border border-[var(--line)] bg-white hover:border-[#d36b46] transition-colors"><Printer className="w-3.5 h-3.5" /> Print</button>
            </div>
          </div>
        </div>

        <div className="hidden sm:grid grid-cols-[28px_60px_1fr] gap-3 px-6 sm:px-10 pt-3 pb-1 label-mono text-[var(--text-2)]">
          <span>Done</span><span>Time</span><span>Event / Call-back notes</span>
        </div>

        <div className="px-6 sm:px-10 pb-8" style={{ backgroundImage: "repeating-linear-gradient(#fdfbf0, #fdfbf0 39px, #dfe6e0 39px, #dfe6e0 40px)" }}>
          {rows.length === 0 && <p className="py-10 text-sm text-[var(--text-2)]">No service events yet. Add service loops on the Clients tab and they'll appear here as your daily agenda.</p>}
          {rows.map((r, i) => {
            const m = rowMiles(r);
            return (
              <div
                key={r.item_id} data-testid="planner-row"
                onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(i)}
                className={`relative grid grid-cols-[24px_46px_1fr] sm:grid-cols-[28px_60px_1fr] gap-2 sm:gap-3 items-end ${dragI === i ? "opacity-50" : ""}`}
                style={{ height: 40 }}
              >
                <button
                  data-testid="planner-drag-handle" draggable
                  onDragStart={() => setDragI(i)} onDragEnd={() => setDragI(null)}
                  title="Drag to reorder"
                  className="print-hide absolute -left-5 top-1/2 -translate-y-1/2 text-[var(--text-2)] hover:text-[#d36b46] cursor-move opacity-60 hover:opacity-100 transition-opacity"
                >
                  <GripVertical className="w-4 h-4" />
                </button>
                <button data-testid="planner-done" onClick={() => toggleDone(i, r)} className={`w-5 h-5 mb-1.5 border-2 flex items-center justify-center shrink-0 transition-colors ${r.done ? "bg-[#2A4B46] border-[#2A4B46]" : "border-[var(--text-2)] hover:border-[#2A4B46] bg-white/70"}`}>{r.done && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}</button>
                <span className="font-mono text-xs text-[#2A4B46] pb-1.5">{r.time}</span>
                <div className="min-w-0 flex items-baseline gap-2 pb-1">
                  <span className={`font-medium whitespace-nowrap ${r.done ? "line-through text-[var(--text-2)]" : "text-[#1A1A18]"}`}>{r.title}{r.client_name ? ` · ${r.client_name}` : ""}</span>
                  {r.source === "reminder" && <span data-testid="planner-reminder-badge" className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FFC43D]/25 text-[#946200] border border-[#FFC43D]/60 print-hide">Reminder</span>}
                  <input data-testid="planner-note" value={r.note || ""} onChange={(e) => setField(i, "note", e.target.value)} onBlur={() => commit(r)} placeholder="call-back / notes…" className="bg-transparent outline-none text-sm text-[var(--text-2)] w-full" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal open={reportOpen} onClose={() => setReportOpen(false)} testid="mileage-report" subtitle="Mileage" title="Mileage Report">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="label-mono text-[var(--text-2)]">From</span>
              <input data-testid="report-start" type="date" value={rStart} onChange={(e) => setRStart(e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
            </div>
            <div>
              <span className="label-mono text-[var(--text-2)]">To</span>
              <input data-testid="report-end" type="date" value={rEnd} onChange={(e) => setREnd(e.target.value)} className="mt-2 w-full h-11 px-3 border border-[var(--line)] bg-white outline-none focus:border-[var(--accent)]" />
            </div>
          </div>
          <button data-testid="report-generate" onClick={runReport} className="w-full h-10 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors text-sm">Generate</button>

          {report && (
            <div data-testid="report-result" className="border border-[var(--line)]">
              <div className="flex items-center justify-between p-4 bg-[#2A4B46] text-[#F9F9F7]">
                <span className="label-mono">Total miles · {report.days} active day(s)</span>
                <span className="font-mono text-xl text-[#E37B5B]" data-testid="report-total">{money(report.total_miles)}</span>
              </div>
              <div className="max-h-52 overflow-auto scroll-thin divide-y divide-[var(--line)]">
                {report.rows.length === 0 && <p className="p-4 text-sm text-[var(--text-2)]">No mileage logged in this range.</p>}
                {report.rows.map((r) => (
                  <div key={r.date} className="flex items-center justify-between px-4 py-2 text-sm">
                    <span className="font-mono">{r.date}</span>
                    <span className="text-[var(--text-2)]">{r.items} stop(s)</span>
                    <span className="font-mono text-[#d36b46]">{r.miles}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <button data-testid="report-csv" onClick={reportCsv} disabled={!report} className="flex-1 h-11 flex items-center justify-center gap-1 border border-[var(--line)] hover:border-[var(--accent)] transition-colors disabled:opacity-40"><Download className="w-4 h-4" /> Export CSV</button>
            <button data-testid="report-print" onClick={printReport} disabled={!report} className="flex-1 h-11 flex items-center justify-center gap-1 bg-[var(--accent)] text-white hover:opacity-90 transition-opacity disabled:opacity-40"><Printer className="w-4 h-4" /> Print / PDF</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
